import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { CustomerRemote } from "../../infrastructure/customer.remote";
import { CustomerStateService } from "./customer.state.service";
import { switchMap, tap } from "rxjs/operators";
import { CustomerActionsService } from "./customer-actions.service";
import {
  ConfirmNewPhoneNumberRequest,
  CustomerContactData,
  CustomerContactDataDetails
} from "../models/customer.model";
import { SsoAccountFacade } from "../../../sso-account/domain/+state/sso-account.facade";
import { SsoAccountData } from "../../../sso-account/domain/models/sso-account.model";

@Injectable({
  providedIn: "root",
})
export class CustomerFacade {
  constructor(
    private readonly stateService: CustomerStateService,
    private readonly remote: CustomerRemote,
    private readonly actionsService: CustomerActionsService,
    private readonly ssoAccountFacade: SsoAccountFacade
  ) {}

  getCustomerContactDetails$(): Observable<CustomerContactDataDetails> {
    if (this.stateService.isCustomerContactDetailsStateLoaded()) {
      return this.stateService.getCustomerContactDetails$();
    }

    return this.refreshCustomerContactDetails$();
  }

  private refreshCustomerContactDetails$(): Observable<CustomerContactDataDetails> {
    return this.ssoAccountFacade.getCurrentSsoAccount$().pipe(
      switchMap((currentSsoAccount: SsoAccountData) =>
        this.remote.getPersonalInformation(currentSsoAccount.id)
      ),
      tap((details: CustomerContactDataDetails) =>
        this.stateService.setCustomerContactDetails(details)
      ),
      switchMap(() => this.stateService.getCustomerContactDetails$())
    );
  }

  updateCustomerContactDetails(
    customerId: number,
    contactData: CustomerContactData
  ): Observable<void> {
    return this.remote
      .updatePersonalInformation(customerId, contactData)
      .pipe(
        tap(() => this.refreshCustomerContactDetails$()),
        tap(() => this.ssoAccountFacade.clearSsoAccountCache())
      );
  }

  confirmMobilePhoneNumberChange(
    request: ConfirmNewPhoneNumberRequest
  ): Observable<any> {
    return this.actionsService
      .confirmMobilePhoneNumberChange(request)
      .pipe(
        tap(() => this.clearCustomerCache()),
        tap(() => this.ssoAccountFacade.clearSsoAccountCache())
      );
  }

  discardMobilePhoneNumberChange(customerId: number): Observable<any> {
    return this.remote
      .discardMobilePhoneNumberChange(customerId)
      .pipe(
        tap(() => this.clearCustomerCache()),
        tap(() => this.ssoAccountFacade.clearSsoAccountCache())
      );
  }

  redirectToDashboard(): void {
    return this.actionsService.redirectToDashboard();
  }

  clearCustomerCache(): void {
    return this.stateService.clearState();
  }

  getPendingMsaApplication$(): Observable<number> {
    return this.ssoAccountFacade.getCurrentSsoAccount$()
      .pipe(
        switchMap((currentSsoAccount: SsoAccountData) =>
          this.remote.getPendingMsaApplication(currentSsoAccount.id)
        )
      );
  }
}
